import type { OrgKey } from '@compass/client/types/org_key';
import { useDeleteOrgKey } from '@hooks/useOrgKeys';
import { getErrorMessage } from '@utils/errorMessages';
import { Loader2 } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface DeleteOrgKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgKey: OrgKey;
}

export function DeleteOrgKeyDialog({ open, onOpenChange, orgKey }: DeleteOrgKeyDialogProps) {
  const deleteMutation = useDeleteOrgKey();

  const handleDelete = () => {
    if (!orgKey.organization_id || !orgKey.org_key_id) return;
    deleteMutation.mutate(
      { organization_id: orgKey.organization_id, org_key_id: orgKey.org_key_id },
      {
        onSuccess: () => {
          onOpenChange(false);
          toast.success('API key deleted');
        },
        onError: err => {
          toast.error(getErrorMessage(err) || 'Failed to delete API key');
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Delete API Key</DialogTitle>
          <DialogDescription className="pt-2">
            Are you sure you want to delete{' '}
            <span className="font-medium text-foreground">{orgKey.name}</span>? This key stops
            working immediately.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
